import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { ModalMode } from '@lib/components/Modal/enums';
import { BaseItem } from '@lib/components/Table/types';
import TableMultiselect, {
  TableMultiselectHandle,
  TableMultiselectProps,
  tableMultiselectModalContentClassName,
} from './TableMultiselect';

/**
 * The standalone, self-contained picker: `TableMultiselect`'s body inside a
 * full-screen `Modal` with its own Cancel / Confirm selection buttons. Use a
 * bare `TableMultiselect` instead when the picker is one step of a larger
 * modal flow that owns the chrome (see `TaskAndCompletionsModal`).
 */
interface ModalTableMultiselectProps<T extends BaseItem> extends Omit<
  TableMultiselectProps<T>,
  'ref' | 'onSelectionConfirmed'
> {
  caption?: string;
  renderAfterCaption?: React.ReactNode;
  onClose: () => void;
}

function ModalTableMultiselect<T extends BaseItem>({
  caption = '',
  renderAfterCaption,
  isOpen,
  onClose,
  ...tableProps
}: ModalTableMultiselectProps<T>) {
  const { t } = useTranslation();
  const tableRef = useRef<TableMultiselectHandle>(null);

  const handleModalClose = () => {
    // Clear selections when modal closes without confirming
    tableRef.current?.clearSelection();
    onClose();
  };

  const actions = (
    <ActionsRow
      actions={[
        <Button
          key="secondary"
          buttonText={t('cancel')}
          buttonType={ButtonTypes.secondaryOutlined}
          onClick={handleModalClose}
          fullWidth
        />,
        <Button
          key="primary"
          buttonText={t('confirm-selection')}
          buttonType={ButtonTypes.primaryFilled}
          onClick={() => tableRef.current?.confirmSelection()}
          fullWidth
        />,
      ]}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      caption={caption}
      renderAfterCaption={renderAfterCaption}
      mode={ModalMode.FullScreen}
      modalContentClassName={tableMultiselectModalContentClassName}
      fixedActions={actions}
    >
      <TableMultiselect<T>
        {...(tableProps as TableMultiselectProps<T>)}
        isOpen={isOpen}
        ref={tableRef}
        onSelectionConfirmed={onClose}
      />
    </Modal>
  );
}

export default memo(ModalTableMultiselect) as <T extends BaseItem>(
  props: ModalTableMultiselectProps<T>,
) => React.ReactElement;

export { default as TableMultiselect } from './TableMultiselect';
export type {
  TableMultiselectHandle,
  TableMultiselectProps,
  VisibleViaQrColumnConfig,
} from './TableMultiselect';
export { tableMultiselectModalContentClassName } from './TableMultiselect';
export { useModalTableMultiselectStates } from './hooks/useModalTableMultiselectStates';
export { default as useModalTableMultiselect } from './hooks/useModalTableMultiselect';
export type {
  ModalTableMultiselectStates,
  FilterState,
  ActiveTabState,
} from './hooks/useModalTableMultiselectStates';
export { default as useModalTableAllTabManager } from './hooks/useModalTableAllTabManager';
